import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StoreIcon from '@mui/icons-material/Store';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import axiosInstance from '../../lib/axios';
import type { Location } from '../../types';

export const LocationShow = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: location, isLoading, error } = useQuery<Location>({
    queryKey: ['location', id],
    queryFn: async () => {
      const response = await axiosInstance.get(`/locations/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">
          {error instanceof Error ? error.message : 'Failed to load location'}
        </Alert>
      </Box>
    );
  }

  if (!location) {
    return (
      <Box>
        <Alert severity="info">Location not found</Alert>
      </Box>
    );
  }

  const CapabilityItem = ({ label, enabled }: { label: string; enabled: boolean }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {enabled ? (
        <CheckCircleIcon color="success" fontSize="small" />
      ) : (
        <CancelIcon color="disabled" fontSize="small" />
      )}
      <Typography variant="body2" color={enabled ? 'text.primary' : 'text.disabled'}>
        {label}
      </Typography>
    </Box>
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {location.type === 'store' ? (
            <StoreIcon sx={{ fontSize: 32 }} />
          ) : (
            <WarehouseIcon sx={{ fontSize: 32 }} />
          )}
          <Typography variant="h4">Location Details</Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/locations')}
          >
            Back
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/locations/edit/${id}`)}
          >
            Edit
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Stack gap={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Name
                  </Typography>
                  <Typography variant="body1">{location.name}</Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 4 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Type
                    </Typography>
                    <Chip
                      icon={location.type === 'store' ? <StoreIcon /> : <WarehouseIcon />}
                      label={location.type === 'store' ? 'Store' : 'Warehouse'}
                      color={location.type === 'store' ? 'primary' : 'secondary'}
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Status
                    </Typography>
                    <Chip
                      label={location.isActive ? 'Active' : 'Inactive'}
                      color={location.isActive ? 'success' : 'default'}
                    />
                  </Box>
                </Box>

                {location.code && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Code
                    </Typography>
                    <Typography variant="body1">{location.code}</Typography>
                  </Box>
                )}

                {location.description && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Description
                    </Typography>
                    <Typography variant="body1">{location.description}</Typography>
                  </Box>
                )}

                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Organization
                  </Typography>
                  <Typography variant="body1">
                    {(location as any).organization?.name || 'N/A'}
                  </Typography>
                </Box>

                <Divider />

                <Typography variant="subtitle1" fontWeight="bold">
                  Address
                </Typography>

                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Address
                  </Typography>
                  <Typography variant="body1">{location.address || 'N/A'}</Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      City
                    </Typography>
                    <Typography variant="body1">{location.city || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Wilaya
                    </Typography>
                    <Typography variant="body1">{location.wilaya || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Country
                    </Typography>
                    <Typography variant="body1">{location.country}</Typography>
                  </Box>
                </Box>

                <Divider />

                <Typography variant="subtitle1" fontWeight="bold">
                  Contact
                </Typography>

                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Phone
                    </Typography>
                    <Typography variant="body1">{location.phone || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Email
                    </Typography>
                    <Typography variant="body1">{location.email || 'N/A'}</Typography>
                  </Box>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Created At
                  </Typography>
                  <Typography variant="body1">
                    {new Date(location.createdAt).toLocaleString('fr-DZ')}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Capabilities Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Capabilities
              </Typography>
              <Stack gap={1.5}>
                <CapabilityItem
                  label="Can sell to customers"
                  enabled={location.canSellToCustomers}
                />
                <CapabilityItem
                  label="Can fulfill online orders"
                  enabled={location.canFulfillOnlineOrders}
                />
                <CapabilityItem
                  label="Can receive transfers"
                  enabled={location.canReceiveTransfers}
                />
                <CapabilityItem
                  label="Can send transfers"
                  enabled={location.canSendTransfers}
                />
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Currency
              </Typography>
              <Typography variant="body1">
                {location.currency === 'DZD' ? 'Algerian Dinar (DZD)' :
                 location.currency === 'EUR' ? 'Euro (EUR)' :
                 location.currency || 'Not set'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
